import { Controller } from 'stimulus'
import numeral from 'numeral'
import 'numeral/locales'

export default class extends Controller {
  static targets = ['priceTableSelect']

  connect() {
    numeral.locale('pt-BR')
    this._boundLoad = this._onTurbolinksLoad.bind(this)
    document.addEventListener('turbolinks:load', this._boundLoad, { once: true })
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    document.removeEventListener('turbolinks:load', this._boundLoad)
  }

  _onTurbolinksLoad() {
    if (!this.hasPriceTableSelectTarget) return

    const select = this.priceTableSelectTarget

    select.addEventListener('change', () => {
      if (select.value) this.fetchAndUpdatePrices(select.value)
    })

    this._observer = new MutationObserver(() => {
      clearTimeout(this._debounce)
      this._debounce = setTimeout(() => {
        const value = select.value
        if (value) this.fetchAndUpdatePrices(value)
      }, 150)
    })
    this._observer.observe(select, { childList: true })
  }

  async fetchAndUpdatePrices(priceTableId) {
    const rows = Array.from(this.element.querySelectorAll('tr.nested-fields')).filter(row => {
      return row.style.display !== 'none'
    })
    if (!rows.length) return

    const productSkuIds = rows.map(row => {
      const select = row.querySelector('[data-attr="product_sku_id"]')
      return select ? select.value : null
    }).filter(Boolean)

    if (!productSkuIds.length) return

    const params = new URLSearchParams({ price_table_id: priceTableId })
    productSkuIds.forEach(id => params.append('product_sku_ids[]', id))

    try {
      const response = await fetch(`/orders/budgets/item_prices?${params}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
      if (!response.ok) return

      const { prices } = await response.json()

      rows.forEach(row => {
        const skuSelect = row.querySelector('[data-attr="product_sku_id"]')
        if (!skuSelect || !skuSelect.value) return

        const priceFromTable = prices[skuSelect.value]
        const newPrice = (priceFromTable === undefined || priceFromTable === null) ? 0 : priceFromTable

        const unitPriceInput = row.querySelector('input[data-target*="calc-skus-prices.value"]')
        if (!unitPriceInput) return

        unitPriceInput.value = newPrice
        unitPriceInput.dispatchEvent(new Event('change', { bubbles: true }))

        const priceTag = unitPriceInput.closest('td').querySelector('.price-tag')
        if (priceTag) priceTag.value = numeral(String(newPrice).replace('.', ',')).format('0,0.00')
      })

      const totalInput = this.element.querySelector('input[data-target*="calc-skus-prices.totalValue"]')
      if (totalInput && totalInput.value) this._redistributePayments(totalInput.value)
    } catch (e) {
      console.error('[recalculate-item-prices]', e)
    }
  }

  _redistributePayments(newTotalFormatted) {
    const newTotal = numeral(newTotalFormatted).value()
    if (newTotal == null) return

    const paymentRows = Array.from(
      this.element.querySelectorAll('tr[data-target*="box-payment.value"]')
    ).filter(row => getComputedStyle(row).display !== 'none')

    if (!paymentRows.length) return

    if (paymentRows.length === 1) {
      this._setPaymentAmount(paymentRows[0], newTotal)
      return
    }

    const currentAmounts = paymentRows.map(row => Number(row.dataset.boxInstallmentCalcTotalValue) || 0)
    const currentSum = currentAmounts.reduce((a, b) => a + b, 0)

    let accumulated = 0
    paymentRows.forEach((row, i) => {
      const isLast = i === paymentRows.length - 1
      const proportion = currentSum > 0 ? currentAmounts[i] / currentSum : 1 / paymentRows.length
      const value = isLast
        ? Math.round((newTotal - accumulated) * 100) / 100
        : Math.round(newTotal * proportion * 100) / 100
      accumulated += value
      this._setPaymentAmount(row, value)
    })
  }

  _setPaymentAmount(row, amount) {
    const visibleInput = row.querySelector('input[data-target="mask.input"]')
    if (!visibleInput) return

    visibleInput.value = numeral(amount).format('0,0.00')
    visibleInput.dispatchEvent(new Event('input', { bubbles: true }))
  }
}
